import type { ReactNode } from 'react';
import { TaskActionBar, TaskHeader, TaskNotice, TaskShell } from '@/features/contribution-kit';
import { Icon } from '@/lib/icons';
import type { Question } from '@/types/theme.types';
import { fill } from '../copy';
import type { InitialDataCopy } from '../initial-data-copy';
import { initialDataActions, type RecordingControls } from '../initial-data.view';
import type { InitialDataMode, SessionTally } from '../types';
import { QuestionCard } from './QuestionCard';
import { QuestionMarkers } from './QuestionMarkers';

export type SessionView = {
  current: Question | null;
  currentIndex: number;
  isComplete: boolean;
  isCurrentDone: boolean;
  done: ReadonlySet<string>;
  skipped: ReadonlySet<string>;
  submittedThisSession: number;
  tally: SessionTally;
  skip: () => void;
  previous: () => void;
  reviewSkipped: () => void;
};

type InitialDataSessionProps = {
  copy: InitialDataCopy;
  mode: InitialDataMode;
  question: Question;
  questions: readonly Question[];
  session: SessionView;
  sectorName: string;
  languageName: string;
  notices?: ReactNode;
  answerPanel: ReactNode;
  isSubmitting: boolean;
  canSubmit: boolean;
  blocked: boolean;
  recording?: RecordingControls;
  onSubmit: () => void;
  onPause: () => void;
  onChangeSector: () => void;
};

/** One question at a time: the question card, the answer card and the bottom bar. */
export function InitialDataSession(props: InitialDataSessionProps) {
  const { copy, session, question } = props;
  const pauseDisabled = props.isSubmitting || props.blocked;

  return (
    <TaskShell
      width="wide"
      header={
        <TaskHeader
          task={props.mode === 'audio' ? 'answerAudio' : 'answerText'}
          index={session.currentIndex}
          total={session.tally.total}
          positionLabel={
            <span data-testid="initial-data-position">
              {fill(copy.questionXofY, { current: session.currentIndex + 1, total: session.tally.total })}
            </span>
          }
          progress={session.tally.percent}
          itemId={question.id}
          onBack={props.onChangeSector}
          meta={
            <>
              <span className="max-w-[12rem] truncate sm:max-w-xs">{[props.sectorName, props.languageName].filter(Boolean).join(' · ')}</span>
              <button
                type="button"
                onClick={props.onPause}
                disabled={pauseDisabled}
                data-testid="initial-data-pause"
                className="inline-flex h-8 items-center gap-1.5 rounded-full border border-border/60 bg-surface px-3 text-xs font-medium text-foreground transition-colors hover:border-brand/40 hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-45"
              >
                <Icon name="pause" className="size-3.5" />
                {copy.pause}
              </button>
            </>
          }
        />
      }
      notices={
        props.notices || session.isCurrentDone ? (
          <>
            {props.notices}
            {session.isCurrentDone ? <TaskNotice tone="success" title={copy.alreadyDone} /> : null}
          </>
        ) : undefined
      }
      actionBar={
        <TaskActionBar
          actions={initialDataActions({
            copy,
            canGoPrevious: session.currentIndex > 0,
            canSubmit: props.canSubmit,
            isSubmitting: props.isSubmitting,
            isCurrentDone: session.isCurrentDone,
            blocked: props.blocked,
            onPrevious: session.previous,
            onSkip: session.skip,
            onSubmit: props.onSubmit,
            recording: props.recording,
          })}
        />
      }
    >
      <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
        <QuestionCard
          copy={copy}
          question={question}
          footer={
            <QuestionMarkers
              copy={copy}
              questions={props.questions}
              currentIndex={session.currentIndex}
              done={session.done}
              skipped={session.skipped}
              tally={session.tally}
            />
          }
        />
        {props.answerPanel}
      </div>
    </TaskShell>
  );
}
